import { Play, TrendingUp, Eye, CheckCircle } from 'lucide-react';
import { useState, useEffect } from 'react';
import { ContentRail } from './ContentRail';
import { formatDuration } from '../../utils/homeHelpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function TrendingNowSection({ navigate, onPlayVideo }) {
  const [trending, setTrending] = useState([]);
  const [windowLabel, setWindowLabel] = useState('');

  useEffect(() => {
    fetch(`${API}/content/trending?limit=20`)
      .then(r => r.json())
      .then(data => {
        setTrending(data.trending || []);
        const w = data.window || '24h';
        setWindowLabel(w === '24h' ? 'Last 24 hours' : w === '7d' ? 'This week' : 'All time');
      })
      .catch(() => {});
  }, []);

  if (!trending.length) return null;

  return (
    <ContentRail
      icon={<div className="w-9 h-9 rounded-lg bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center shrink-0"><TrendingUp className="w-4 h-4 text-white" /></div>}
      title="Trending Now"
      subtitle={`${windowLabel} \u00B7 ${trending.length} clips`}
      seeAllPath="/highlights"
      navigate={navigate}
      testId="trending-now-section"
    >
      {trending.map((item, idx) => (
        <div
          key={item.id}
          onClick={() => {
            if (item.source === 'youtube' && item.youtube_video_id) onPlayVideo(item);
            else navigate(`/match/${item.id}`);
          }}
          className="flex-shrink-0 w-[260px] sm:w-[300px] cursor-pointer group snap-start"
          data-testid={`trending-card-${item.id}`}
        >
          <div className="relative aspect-video rounded-xl overflow-hidden mb-2.5">
            <img src={item.thumbnail_url} alt={item.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
            {/* Rank */}
            <div className="absolute top-2 left-2">
              <div className={`w-6 h-6 rounded-md flex items-center justify-center text-[10px] font-black ${
                idx === 0 ? 'bg-gradient-to-br from-yellow-400 to-orange-500 text-black'
                : idx === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-400 text-black'
                : idx === 2 ? 'bg-gradient-to-br from-orange-600 to-orange-800 text-white'
                : 'bg-white/10 backdrop-blur-sm text-white/70 border border-white/10'
              }`}>{idx + 1}</div>
            </div>
            {/* Views */}
            <div className="absolute top-2 right-2 flex items-center gap-1 px-1.5 py-0.5 bg-black/60 backdrop-blur-sm rounded-full border border-white/10">
              <Eye className="w-2.5 h-2.5 text-orange-400" />
              <span className="text-white text-[9px] font-bold">{item.view_count}</span>
            </div>
            {item.duration && (
              <div className="absolute bottom-2 right-2 px-1.5 py-0.5 bg-black/80 text-white text-[9px] font-medium rounded">{formatDuration(item.duration)}</div>
            )}
            {item.competition_name && (
              <div className="absolute bottom-2 left-2 flex items-center gap-1.5">
                <span className="px-1.5 py-0.5 bg-black/60 backdrop-blur-sm border border-white/10 rounded-full text-emerald-400 text-[8px] font-bold uppercase tracking-wider">{item.competition_name}</span>
                {item.has_real_data && (
                  <span className="flex items-center gap-0.5 px-1.5 py-0.5 bg-emerald-500/20 backdrop-blur-sm border border-emerald-500/25 rounded-full" data-testid={`verified-trending-${item.id}`}>
                    <CheckCircle className="w-2.5 h-2.5 text-emerald-400" />
                    <span className="text-emerald-400 text-[8px] font-bold">Verified</span>
                  </span>
                )}
              </div>
            )}
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <div className="w-11 h-11 bg-orange-500 rounded-full flex items-center justify-center shadow-lg shadow-orange-500/30">
                <Play className="w-4 h-4 text-white" fill="currentColor" />
              </div>
            </div>
          </div>
          <h3 className="text-white font-semibold text-sm group-hover:text-orange-400 transition-colors line-clamp-2">{item.teams || item.title}</h3>
          <p className="text-gray-500 text-xs mt-0.5 line-clamp-1">{item.view_count} view{item.view_count > 1 ? 's' : ''}{item.duration ? ` \u00B7 ${formatDuration(item.duration)}` : ''}{item.competition_name ? ` \u00B7 ${item.competition_name}` : ''}</p>
        </div>
      ))}
    </ContentRail>
  );
}
